// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import { findKey } from 'lodash';
import {
    DefaultButton,
    IColumn,
    SpinnerSize
} from 'office-ui-fabric-react';

import {
    accessoryRepairSelector,
    allSelectedOfferSelector,
    deviceRepairSelector,
    isFullDeviceRepairSelector,
    parentDeviceOfferSelector,
    partAndAccessoryOfferSelector,
    isSoftwareOrderSelector,
    softwareRepairOfferSelector
} from './../../../store/orderCreation/selectors';
import {
    GET_REPAIR_OFFER,
    Progress,
    saveSelectedOffer,
    updateCreateOrderProgress,
    resetRegistrationData,
    GET_REGISTER_INFO_DATA,
    resetParts,
    resetPartsStatus
} from './../../../store/orderCreation';
import { actionStatusSelector } from './../../../store/actionStatus/selectors';
import { selectedCountryInfoSelector } from './../../../store/countries/selectors';
import { LoadingState, resetActionsData } from './../../../store/actionStatus';
import { resetAddressData } from './../../../store/address';

import Offer from './../../../components/Offer/Offer';
import BusySpinner from './../../../components/BusySpinner';

import Styles from './styles.module.scss';
import { logPageAction } from 'src/lib/ApplicationInsights';

interface BodyTextType {
    description: string;
    title: string;
}

export default React.forwardRef(ServiceOptions);

function ServiceOptions(props: any, ref: any) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const isFullDeviceRepair: boolean = useSelector(state => isFullDeviceRepairSelector(state));
    const isSoftwareOrder: boolean = useSelector(state => isSoftwareOrderSelector(state));
    const deviceRepairItems = useSelector(state => deviceRepairSelector(state));
    const accessoryRepairItems = useSelector(state => accessoryRepairSelector(state));

    const countryInfo = useSelector(state => selectedCountryInfoSelector(state));
    const countryName = countryInfo && countryInfo.countryName;

    const parentDeviceOffer: any = useSelector(state => parentDeviceOfferSelector(state));
    const softwareRepairOffer: any = useSelector(state => softwareRepairOfferSelector(state));
    const partAndAccessoryOffers: [] = useSelector(state => partAndAccessoryOfferSelector(state));

    const allSelectedRepairOffer: any = useSelector(state => allSelectedOfferSelector(state));

    const offerCallStatus = useSelector(state => actionStatusSelector(state)(GET_REPAIR_OFFER));

    const columnHeaders: IColumn[] = [
        {
            key: 'option',
            fieldName: 'option',
            name: t('options'),
            minWidth: 650,
            maxWidth: 694,
        },
        {
            key: 'cost',
            fieldName: 'cost',
            name: t('cost'),
            minWidth: 100,
            maxWidth: 130,
        },
    ];

    if (offerCallStatus.status === LoadingState.started) {
        return (
            <BusySpinner
                className={Styles.spinner}
                messageKey={t('getting_offers')}
                size={SpinnerSize.large}
            />
        );
    }

    if (offerCallStatus.status === LoadingState.rejected) {
        return (
            <div>{t('fetch_offer_error')}</div>
        );
    }

    function renderParentOffer() {
        if (!(isFullDeviceRepair) ||
            !parentDeviceOffer ||
            !Object.keys(parentDeviceOffer).length) {
            if (!isSoftwareOrder ||
                !softwareRepairOffer ||
                !Object.keys(softwareRepairOffer).length) {
                return null;
            }
        }

        const { offerProperties, messages, offers } = (Object.keys(parentDeviceOffer).length) ? parentDeviceOffer : softwareRepairOffer;


        const headerTitle = {
            description: t(offerProperties.warrantyStatus),
            title: `${offerProperties.brandName} ${offerProperties.productSeries}`,
        };

        const bodyText: BodyTextType[] | [] = messages?.map((message: any) => ({
            description: message.value,
            title: message.title,
        })) ?? [];

        const { symptomCode } = offerProperties;
        const selectedOffer = allSelectedRepairOffer && allSelectedRepairOffer[symptomCode] || [];

        const selectedItem = [
            {
                symptomCode,
                option: {
                    description: selectedOffer.description,
                    title: selectedOffer.name,
                    offerId: selectedOffer.offerId,
                },
                cost: `${parseFloat(selectedOffer.price || 0).toFixed(2)} ${selectedOffer.currency
                    }`,
                data: selectedOffer,
            },
        ];

        const defaultOffer = {
            symptomCode,
            option: {
                toolTip: t('do_not_repair'),
                title: t('do_not_repair'),
                description: t('do_not_repair_description'),
                offerId: t('do_not_repair'),
            },
            cost: '',
            data: {},
        };

        const offersItems = offers.length ? [
            defaultOffer,
            ...offers.map((offer: any) => ({
                symptomCode,
                option: {
                    description: offer.description,
                    title: offer.name,
                    offerId: offer.offerId,
                },
                cost: `${parseFloat(offer.price || 0).toFixed(2)} ${offer.currency}`,
                data: offer,
            })),
        ] : [];

        return (
            <Offer
                collapsibleBody={bodyText}
                collapsibleHeader={headerTitle}
                columnHeaders={columnHeaders}
                items={offersItems}
                readonly={props.readonly}
                selectedDetailsKey={selectedItem}
                noRecordMessageKey={t('no_offer_found')}
                onChange={onOfferSelectionChange}
                tableName={t('device')}
                offerKey={0}
            />
        );
    }

    function renderPartOffer() {
        if (isFullDeviceRepair) {
            return null;
        }

        if (!partAndAccessoryOffers || !partAndAccessoryOffers.length) {
            return null;
        }

        const deviceItemsPartNumber = Object.keys(deviceRepairItems);

        const partOffers = partAndAccessoryOffers.filter((offer: any): any => deviceItemsPartNumber.includes(offer.offerProperties.partCode));

        return partOffers.map((offerData: any, key: number) => {
            const { offerProperties, messages, offers } = offerData;

            const sku = findKey(deviceRepairItems, (item) => item.symptomCode === offerData.offerProperties.partCode) || '';
            const deviceInfo: any = deviceRepairItems[sku];

            const headerTitle = {
                description: t(offerProperties.warrantyStatus),
                title: deviceInfo ? deviceInfo.name : '',
            };

            const bodyText: BodyTextType[] | [] = messages?.map((message: any) => ({
                description: message.value,
                title: message.title,
            })) ?? [];

            const { partCode } = offerProperties;

            const selectedOffer = (allSelectedRepairOffer && allSelectedRepairOffer[partCode]) || [];

            let selectedItem = [
                {
                    symptomCode: partCode,
                    option: {
                        description: selectedOffer.description,
                        title: selectedOffer.name,
                        offerId: selectedOffer.offerId,
                    },
                    cost: `${parseFloat(selectedOffer.price || 0).toFixed(2)} ${selectedOffer.currency
                        }`,
                    data: selectedOffer,
                },
            ];

            const defaultOffer = {
                symptomCode: partCode,
                option: {
                    toolTip: t('do_not_repair'),
                    title: t('do_not_repair'),
                    description: t('do_not_repair_description'),
                    offerId: t('do_not_repair'),
                },
                cost: '',
                data: {},
            };

            if (typeof allSelectedRepairOffer !== 'undefined') { //default button selected
                if (typeof allSelectedRepairOffer[partCode] !== 'undefined' && Object.keys(allSelectedRepairOffer[partCode]).length === 0) {
                    selectedItem = [defaultOffer];
                }
            }

            const offersItems = offers.length ? [
                defaultOffer,
                ...offers.map((offer: any) => ({
                    symptomCode: partCode,
                    option: {
                        description: offer.description,
                        title: offer.name,
                        offerId: offer.offerId,
                    },
                    cost: `${parseFloat(offer.price || 0).toFixed(2)} ${offer.currency}`,
                    data: offer,
                })),
            ] : [];

            return (
                <div className={Styles.partOfferContainer} key={key}>
                    <Offer
                        collapsibleBody={bodyText}
                        collapsibleHeader={headerTitle}
                        columnHeaders={columnHeaders}
                        items={offersItems}
                        readonly={props.readonly}
                        selectedDetailsKey={selectedItem}
                        noRecordMessageKey={t('no_offer_found')}
                        onChange={onOfferSelectionChange}
                        tableName={t('part_number')}
                        offerKey={key}
                    />
                </div>
            );
        });
    }

    function generateAccessoryOfferTable(accessoriesOffers: any[]) {
        return accessoriesOffers.map((offerData: any, key: number) => {
            const { offerProperties, messages, offers } = offerData;
            const sku = findKey(accessoryRepairItems, (item: any) => item.data.symptomCode === offerData.offerProperties.accessoryType) || '';
            const deviceInfo: any = accessoryRepairItems[sku];

            const headerTitle = {
                description: t(offerProperties.warrantyStatus),
                title: deviceInfo.data.name,
            };

            const bodyText: BodyTextType[] | [] = messages?.map((message: any) => ({
                description: message.value,
                title: message.title,
            })) ?? [];

            const { accessoryType } = offerProperties;
            const selectedOffer = (allSelectedRepairOffer && allSelectedRepairOffer[accessoryType]) || [];

            const selectedItem = [
                {
                    symptomCode: accessoryType,
                    option: {
                        description: selectedOffer.description,
                        title: selectedOffer.name,
                        offerId: selectedOffer.offerId,
                    },
                    cost: `${parseFloat(selectedOffer.price || 0).toFixed(2)} ${selectedOffer.currency
                        }`,
                    data: selectedOffer,
                },
            ];

            const defaultOffer = {
                symptomCode: accessoryType,
                option: {
                    toolTip: t('do_not_repair'),
                    title: t('do_not_repair'),
                    description: t('do_not_repair_description'),
                    offerId: t('do_not_repair'),
                },
                cost: '',
                data: {},
            };

            const offersItems = offers.length ? [
                defaultOffer,
                ...offers.map((offer: any) => ({
                    symptomCode: accessoryType,
                    option: {
                        description: offer.description,
                        title: offer.name,
                        offerId: offer.offerId,
                    },
                    cost: `${parseFloat(offer.price || 0).toFixed(2)} ${offer.currency}`,
                    data: offer,
                })),
            ] : [];

            return (
                <div className={Styles.partOfferContainer} key={key}>
                    <Offer
                        collapsibleBody={bodyText}
                        collapsibleHeader={headerTitle}
                        columnHeaders={columnHeaders}
                        items={offersItems}
                        readonly={props.readonly}
                        selectedDetailsKey={selectedItem}
                        noRecordMessageKey={t('no_offer_found')}
                        onChange={onOfferSelectionChange}
                        tableName={t('accessory')}
                        offerKey={key}
                    />
                </div>
            );
        });
    }

    function renderAccessoryOffer() {
        if (!partAndAccessoryOffers ||
            !partAndAccessoryOffers.length ||
            !Object.keys(accessoryRepairItems).length) {
            return null;
        }

        const accessoryTypes = Object.keys(accessoryRepairItems).map((key: any) => accessoryRepairItems[key].data.symptomCode);
        const accessoriesOffers = partAndAccessoryOffers.filter((offer: any): any => accessoryTypes.includes(offer.offerProperties.accessoryType));

        return (
            <>
                <h3 className={Styles.accessoryHeading}> {t('accessory')} </h3>
                {
                    generateAccessoryOfferTable(accessoriesOffers)
                }
            </>
        );
    }

    function onOfferSelectionChange(item: any[]) {
        const { symptomCode, data, option } = item && item.length && item[0];

        if (option.offerId) {
            logPageAction('Offer selected', { itemSelected: option.offerId });
        }

        dispatch(saveSelectedOffer({
            symptomCode,
            offer: data,
        }));
    }

    function onChangeSelectionClick() {
        logPageAction('Service Options change selection button clicked');

        dispatch(updateCreateOrderProgress(Progress.RepairOfferSelection));
        dispatch(resetAddressData());
        dispatch(resetRegistrationData());
        dispatch(resetActionsData(GET_REGISTER_INFO_DATA));
        dispatch(resetPartsStatus());
        dispatch(resetParts());
    }

    const changeSelectionClassName = clsx(Styles.changeSelection, {
        [Styles.disabled]: !props.readonly,
    });

    return (
        <div className={Styles.repairItemContainer}>
            <header className={Styles.headerSection}>
                <h2
                    className={Styles.h2Heading}
                    ref={ref}
                    tabIndex={0}
                >{t('service_options')}</h2>
                <DefaultButton
                    allowDisabledFocus={true}
                    ariaLabel={t('change_service_option_selection')}
                    disabled={!props.readonly}
                    className={changeSelectionClassName}
                    text={t('change_selection')}
                    onClick={onChangeSelectionClick}
                />
            </header>
            <div className={Styles.serviceOptionsDesc}>
                <div>{t('service_options_desc')} </div>
                <div className={Styles.country}>{countryName}</div>
            </div>
            <h3 className={Styles.h3Heading}> {t('device')} </h3>
            {renderParentOffer()}
            {renderPartOffer()}
            {renderAccessoryOffer()}
        </div>
    );
}
